import { z } from 'zod';
import { sessionOriginSchema, versionTagSchema } from './versioning.js';

/**
 * The session of ONE person (refonte modulaire §5.2, §5.3, §8.7, §12.1) - the
 * half of it that two lots share.
 *
 * ## Why this file exists before either lot (issue #50)
 *
 * The Expériences lot OWNS the session: a link opens it, the cascade is frozen
 * at that instant, and it carries the current page, the variables and the
 * 24-hour expiry. The Protocoles lot FILLS it: every turn of the conversation
 * (§8.7) hangs off a session id and reads the frozen protocol, persona and
 * criteria versions. Built in parallel without this file, the two lots would
 * each draw a `sessions` table and reconcile them by hand the week of the
 * merge. So the shapes both sides read are here, and the ones only one side
 * reads - the player's view of a page, the turn - stay in their lot. The
 * decisions this contract rests on are recorded in the spec by issue #51.
 */

/* --------------------------------------------------------------- the status */

/**
 * What the base STORES. `expired` is never written: the 24 hours of §5.3 are
 * derived at read time from `startedAt`, at the régime the chat already applies
 * to its window, with no scheduled task. `interrupted` (issue #199,
 * interruption.md §3) is the fifth: an operator's gesture says « cette session
 * est finie », signed (`endedBy`) and final.
 */
export const sessionStoredStatusSchema = z.enum([
  'in-progress',
  'finished',
  'closed',
  'interrupted',
]);
export type SessionStoredStatus = z.infer<typeof sessionStoredStatusSchema>;

/**
 * What a screen READS (§12.1): the four stored states plus the derived one.
 * French labels live on the screen: `finished` is « terminée » (the last page
 * reached), `closed` « fermée » (a fallback of a step or a guard, with its
 * cause), `interrupted` « interrompue » (an operator's gesture, issue #199),
 * `expired` « expirée ».
 */
export const sessionStatusSchema = z.enum([
  ...sessionStoredStatusSchema.options,
  'expired',
] as const);
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

/**
 * The window of §5.3: a session ends 24 hours after its start, whatever page it
 * is on. In HOURS and in `shared`, at the régime of `CHAT_LINK_TTL_HOURS`: a
 * screen says the number out loud (the sober expiry screen, the « Reprendre »
 * page), and the backend derives the milliseconds beside the function that
 * computes `expiresAt`, the way `chat/expiry.ts` does for the chat.
 */
export const SESSION_TTL_HOURS = 24;

/* ------------------------------------------------------------- the resolution */

/**
 * The cascade FROZEN at the start (§4, §5.2): version identifiers, never tags.
 *
 * A conversation in progress never changes rules, even when somebody passes a
 * version to production while it plays. Nullable where the experience may carry
 * nothing: a protocol page left empty, a persona chosen « plus tard ». The
 * criteria are those the resolved protocol cites, in no particular order.
 */
export const frozenResolutionSchema = z
  .object({
    experienceVersionId: z.string().min(1),
    protocolVersionId: z.string().min(1).nullable(),
    personaVersionId: z.string().min(1).nullable(),
    criterionVersionIds: z.array(z.string().min(1)),
  })
  .strict();
export type FrozenResolution = z.infer<typeof frozenResolutionSchema>;

/* -------------------------------------------------------------- the variables */

/**
 * One variable of the session (§8.6).
 *
 * The record that holds them is keyed by the identifier of the block that
 * wrote the value - or of the page, for the choice retained among several
 * buttons - and the NAME is frozen beside the value: it is a label, unique in
 * the timeline, and Sessions must read a session whose variable was renamed
 * since. A checkbox writes a boolean, an input or a choice writes a string.
 *
 * Not `.strict()`: it is a stored shape (§4.1).
 */
export const sessionVariableSchema = z.object({
  name: z.string().min(1),
  value: z.union([z.string(), z.boolean()]),
});
export type SessionVariable = z.infer<typeof sessionVariableSchema>;

export const sessionVariablesSchema = z.record(z.string().min(1), sessionVariableSchema);
export type SessionVariables = z.infer<typeof sessionVariablesSchema>;

/* -------------------------------------------------------------- the page walk */

/**
 * ONE page a session actually traversed (§9.1, §12.2): when it was entered,
 * when it was left, and by which button.
 *
 * ## Why the session stores this instead of deriving it
 *
 * A session holds `currentPageId` and nothing behind it. Before this row
 * existed, the only readable fact about a traversal was where it had got to:
 * which pages were seen, in what order, how long each one held somebody, and
 * what they typed on it were gone the moment the advance returned. The turns
 * date the protocol page precisely (§8.7); every OTHER page had no clock at
 * all, so the reader of a session could show a conversation and never a
 * parcours - and a session that stopped before the protocol showed nothing.
 *
 * ## The name travels frozen, like a variable's
 *
 * `sessionVariableSchema` keeps a label beside its value because the timeline
 * may be renamed and Sessions must read a session that ran before the rename.
 * A page's name is the same kind of fact: it is read off the FROZEN version at
 * the moment the page is served, and kept here so no reader resolves it twice
 * - nor reads today's name over yesterday's traversal.
 *
 * `leftAt` is null for the page a session is standing on, including one it will
 * never leave, which is what an expired session looks like. `pressedBlockId` is
 * null when the page's automatic duration ran out rather than a button being
 * pressed (§9.2), and `pressedLabel` carries what that button SAID, for the
 * reason the page's name is here.
 *
 * Not `.strict()`: it is a stored shape (§4.1).
 */
export const sessionPageVisitSchema = z.object({
  pageId: z.string().min(1),
  /** The page's name inside the frozen version, as it was when served. */
  name: z.string().min(1),
  enteredAt: z.string().datetime(),
  leftAt: z.string().datetime().nullable().default(null),
  pressedBlockId: z.string().min(1).nullable().default(null),
  pressedLabel: z.string().nullable().default(null),
});
export type SessionPageVisit = z.infer<typeof sessionPageVisitSchema>;

/**
 * The couple a session RECORDS about its entry (§5.1): the experience, and the
 * pointer it was played under - `production` or `development`.
 *
 * Not a `versionedRefSchema`: a stored reference says `latest` or a version
 * number (vitrine.md §3.1), and a session is not a reference - it froze
 * identifiers, and its `experience_tag` column is what it remembers of the
 * door. Since issue #192 that tag is the SIDE the session played on (`prod` is
 * `production`, `dev` is `development`, `tagOfSide`), and the sessions played
 * before it keep the tag their entry carried. « Recommencer » reads the id.
 */
export const sessionEntrySchema = z
  .object({
    id: z.string().min(1),
    tag: versionTagSchema,
  })
  .strict();
export type SessionEntry = z.infer<typeof sessionEntrySchema>;

/* ---------------------------------------------------------------- the summary */

/**
 * One session as the operator's screens read it (§12.1) - without the pages,
 * without the turns.
 *
 * `entry` is the couple the link entry resolved (§5.1): the experience, under
 * the pointer it was played - `sessionEntrySchema`. `expiresAt` is derived from
 * `startedAt` and served rather than recomputed by every reader.
 * `closeReason` names the rule, condition or guard that closed the session
 * (§8.7), frozen as a label the way a variable's name is, and is null on every
 * other status - never the empty string, so that « no reason » has one
 * spelling. `endedBy` is its twin for the interruption (issue #199). `turnCount` and `costEur` are the count and the SUM of the turns
 * (§8.7), computed by the reader and stored nowhere else: a running total on
 * the session would be a second source of truth for numbers the turns already
 * hold. `deletedAt` is the trash of §12.4: thirty days in it, then gone, and a
 * restoration clears it.
 */
export const sessionSummarySchema = z
  .object({
    id: z.string().min(1),
    status: sessionStatusSchema,
    startedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    closeReason: z.string().min(1).nullable(),
    /**
     * Who interrupted the session (issue #199, interruption.md §9): the email of
     * the operator, or « jeton partagé » - the author `versionAuthor` reads, never
     * a body. Null on every other status. OPTIONAL at the régime of
     * `feedbackSubmittedAt`: an answer from before #199 has no such field and
     * reads null (`row.endedBy ?? null`), and no fixture has to list it.
     */
    endedBy: z.string().min(1).nullable().optional(),
    /** The link the session entered by (§5.1); null for a session no link opened. */
    linkId: z.string().min(1).nullable(),
    entry: sessionEntrySchema,
    frozen: frozenResolutionSchema,
    currentPageId: z.string().min(1).nullable(),
    turnCount: z.number().int().nonnegative(),
    costEur: z.number().nonnegative(),
    deletedAt: z.string().datetime().nullable(),
    /**
     * When the feedback of the page of retour was sent (issue #176,
     * retour-experience.md §5), null while it was not. OPTIONAL like the fields
     * of the view added after the fact: a row written by a server of before
     * #176 has none and reads « not sent ». It rides on the summary so that the
     * list, the detail and the assistant all read ONE fact.
     */
    feedbackSubmittedAt: z.string().datetime().nullable().optional(),
    /**
     * Where the session was born (vitrine.md section 8.2, issue #192): `prod` on
     * the atelier for a session the synchronisation brought up from the vitrine,
     * `dev` otherwise. OPTIONAL at the regime of `feedbackSubmittedAt`: a row built
     * by a server of before the vitrine has none and reads `dev`.
     */
    origin: sessionOriginSchema.optional(),
  })
  .strict();
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

/* ----------------------------------------------------------------- the events */

/**
 * The frames of the SSE channel of a session: `GET /api/s/<jeton>/events`, one
 * permanent connection per open tab, server to client only. Every write stays
 * a POST.
 *
 * The address is under `/api/s/`, the prefix `request-logger.ts` already
 * masks, because the token IS the person's only credential and must not be
 * written into the log; `/api/sessions` stays the operator's list of §12.1 and
 * carries no token.
 *
 * The client re-reads the session by REST on every connection and
 * reconnection, and the channel carries only DELTAS from then on - the
 * doctrine of the chat's socket. Discriminated on `type`; this file names the
 * one frame both lots need. The Protocoles lot added its four beside it in
 * `turn.ts` (issue #63): the typing indicator, the deltas of the interviewer's
 * message, the finished turn, and where the protocol page stands; the
 * Expériences lot adds the page frames if any are needed.
 *
 * **An older tab survives a newer server**, and the schema says so in two ways:
 * a frame whose `type` it does not know fails the union and is DROPPED by the
 * client, never thrown on; a field it does not know on a frame it does know is
 * STRIPPED, which is why no frame here is `.strict()`. The chat's transport
 * chooses strictness to fail loudly on a malformed message; a broadcast channel
 * two lots extend chooses tolerance, or the first added field would blind
 * every tab loaded before the deploy.
 *
 * The status a frame carries is a STORED one (`interrupted` included, issue
 * #199: the gesture pushes it): nothing in the server notices the
 * clock crossing the 24 hours, so `expired` is never pushed - a client with an
 * open channel derives it from `expiresAt` like every other reader.
 */
export const sessionStatusEventSchema = z.object({
  type: z.literal('status'),
  status: sessionStoredStatusSchema,
  /**
   * The cause of a closure (§12.1), null on every other status - and absent
   * reads as null, so a « finished » frame that names no cause is not dropped.
   */
  reason: z.string().min(1).nullable().default(null),
});
export type SessionStatusEvent = z.infer<typeof sessionStatusEventSchema>;

/**
 * The UNION of the frames lives in `turn.ts`, and `sessionEventSchema` is
 * exported from `@matter_extractor/shared` exactly as before (issue #63).
 *
 * It had to be assembled there the day the Protocoles lot added its four
 * frames: the `message` one carries a turn, a turn carries a snapshot, and a
 * snapshot carries the `sessionVariablesSchema` defined above. Composing the
 * union here would make the two files import each other, and a cycle between
 * two modules of Zod constants is not a style question - whichever module Node
 * enters first reads the other's schemas before they are initialised and throws
 * at import time. So the dependency runs ONE way, `turn.ts` reads this file,
 * and the union is assembled at the far end.
 */
